import type { ComponentType, Dispatch, SetStateAction, SVGProps } from "react";
import { FiCreditCard } from "react-icons/fi";
import AdminSection from "../../../shared/components/AdminSection";
import Button from "../../../shared/components/Button";
import InfoTooltip from "../../../shared/components/InfoTooltip";
import Spinner from "../../../shared/components/Spinner";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import type { MyAsaasWalletSettings } from "../services/asaasAdminService";

type AsaasSettingsDraft = {
  environment: "sandbox" | "production";
  platformWalletId: string;
  splitEnabled: boolean;
  platformFeeType: "percent" | "fixed";
  platformFeeValue: number;
};

type AdminPaymentsPanelProps = {
  asaasSettingsDraft: AsaasSettingsDraft;
  isAdmin: boolean;
  isOrganizer: boolean;
  myAsaasWalletDraft: MyAsaasWalletSettings;
  onSaveAsaasSettings: () => Promise<void>;
  onSaveMyWallet: () => Promise<void>;
  paymentsLoading: boolean;
  setAsaasSettingsDraft: Dispatch<SetStateAction<AsaasSettingsDraft>>;
  setMyAsaasWalletDraft: Dispatch<SetStateAction<MyAsaasWalletSettings>>;
};

type IconLike = ComponentType<SVGProps<SVGSVGElement>>;

const PaymentIcon = FiCreditCard as unknown as IconLike;

export default function AdminPaymentsPanel({
  asaasSettingsDraft,
  isAdmin,
  isOrganizer,
  myAsaasWalletDraft,
  onSaveAsaasSettings,
  onSaveMyWallet,
  paymentsLoading,
  setAsaasSettingsDraft,
  setMyAsaasWalletDraft,
}: AdminPaymentsPanelProps) {
  const t = siteTexts.admin;
  const info = siteTexts.common.info;

  return (
    <div className="admin-content">
      {isAdmin && (
        <>
          <AdminSection className="admin-users-dashboard">
            <div className="admin-users-hero">
              <div className="admin-users-hero-copy">
                <span className="admin-users-hero-icon" aria-hidden="true">
                  <PaymentIcon />
                </span>
                <div>
                  <h2 className="info-label">
                    {t.paymentsTab.splitTitle}
                    <InfoTooltip content={info.split} />
                  </h2>
                  <p>Configure split, carteira da plataforma e regras de repasse.</p>
                </div>
              </div>
              <Button
                type="button"
                variant="primary"
                onClick={onSaveAsaasSettings}
                disabled={paymentsLoading}
              >
                {t.paymentsTab.save}
              </Button>
            </div>

            <p className="status-message">{t.paymentsTab.secretsHint}</p>
            <div className="admin-user-grid">
              <label className="field">
                <span>{t.paymentsTab.environment}</span>
                <select
                  value={asaasSettingsDraft.environment}
                  onChange={(event) =>
                    setAsaasSettingsDraft((prev) => ({
                      ...prev,
                      environment: event.target.value as
                        | "sandbox"
                        | "production",
                    }))
                  }
                >
                  <option value="sandbox">Sandbox</option>
                  <option value="production">{t.paymentsTab.production}</option>
                </select>
              </label>
              <label className="field">
                <span>{t.paymentsTab.platformWallet}</span>
                <input
                  type="text"
                  value={asaasSettingsDraft.platformWalletId}
                  onChange={(event) =>
                    setAsaasSettingsDraft((prev) => ({
                      ...prev,
                      platformWalletId: event.target.value,
                    }))
                  }
                />
              </label>
              <label className="field">
                <span className="info-label">
                  {t.paymentsTab.platformFeeType}
                  <InfoTooltip content={info.methodFee} />
                </span>
                <select
                  value={asaasSettingsDraft.platformFeeType}
                  onChange={(event) =>
                    setAsaasSettingsDraft((prev) => ({
                      ...prev,
                      platformFeeType: event.target.value as
                        | "percent"
                        | "fixed",
                    }))
                  }
                >
                  <option value="percent">{t.paymentsTab.percent}</option>
                  <option value="fixed">{t.paymentsTab.fixedFee}</option>
                </select>
              </label>
              <label className="field">
                <span className="info-label">
                  {t.paymentsTab.feeValue}
                  <InfoTooltip content={info.methodFee} />
                </span>
                <input
                  type="number"
                  step="0.01"
                  value={asaasSettingsDraft.platformFeeValue}
                  onChange={(event) =>
                    setAsaasSettingsDraft((prev) => ({
                      ...prev,
                      platformFeeValue: Number(event.target.value || 0),
                    }))
                  }
                />
              </label>
              <label className="field">
                <span className="info-label">
                  {t.paymentsTab.splitEnabled}
                  <InfoTooltip content={info.split} />
                </span>
                <select
                  value={asaasSettingsDraft.splitEnabled ? "true" : "false"}
                  onChange={(event) =>
                    setAsaasSettingsDraft((prev) => ({
                      ...prev,
                      splitEnabled: event.target.value === "true",
                    }))
                  }
                >
                  <option value="true">{t.paymentsTab.active}</option>
                  <option value="false">{t.paymentsTab.inactive}</option>
                </select>
              </label>
            </div>
          </AdminSection>

        </>
      )}

      {isOrganizer && (
        <AdminSection
          title={t.paymentsTab.myWallet}
          action={
            <Button
              type="button"
              variant="primary"
              onClick={onSaveMyWallet}
              disabled={paymentsLoading}
            >
              {t.paymentsTab.save}
            </Button>
          }
        >
          {paymentsLoading ? (
            <Spinner label={t.paymentsTab.loadingSettings} />
          ) : (
            <div className="admin-user-grid">
              <label className="field">
                <span>{t.paymentsTab.walletId}</span>
                <input
                  type="text"
                  value={myAsaasWalletDraft.walletId || ""}
                  onChange={(event) =>
                    setMyAsaasWalletDraft((prev) => ({
                      ...prev,
                      walletId: event.target.value,
                    }))
                  }
                />
              </label>
              <label className="field">
                <span className="info-label">
                  {t.paymentsTab.organizerSplit}
                  <InfoTooltip content={info.split} />
                </span>
                <select
                  value={myAsaasWalletDraft.splitEnabled ? "true" : "false"}
                  onChange={(event) =>
                    setMyAsaasWalletDraft((prev) => ({
                      ...prev,
                      splitEnabled: event.target.value === "true",
                    }))
                  }
                >
                  <option value="true">{t.paymentsTab.active}</option>
                  <option value="false">{t.paymentsTab.inactive}</option>
                </select>
              </label>
            </div>
          )}
        </AdminSection>
      )}
    </div>
  );
}
